import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { promptsAPI } from '../services/api';
import { v4 as uuidv4 } from 'uuid';
import './PromptForm.css';

const PromptForm = () => {
  const { promptName } = useParams();
  const isEdit = !!promptName;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    id: '',
    name: '',
    content: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      loadPrompt();
    } else {
      // Generate new UUID for new prompt
      setFormData((prev) => ({ ...prev, id: uuidv4() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptName, isEdit]);

  const loadPrompt = async () => {
    try {
      setLoadingData(true);
      setError('');
      const prompt = await promptsAPI.get(decodeURIComponent(promptName));
      setFormData({
        id: prompt.id,
        name: prompt.name,
        content: prompt.content,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load prompt');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit) {
        await promptsAPI.update(decodeURIComponent(promptName), {
          name: formData.name,
          content: formData.content,
        });
      } else {
        await promptsAPI.create({
          id: formData.id,
          name: formData.name,
          content: formData.content,
        });
      }
      navigate('/prompts');
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} prompt`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="prompt-form-container">
        <div className="loading">Loading prompt...</div>
      </div>
    );
  }

  return (
    <div className="prompt-form-container">
      <div className="prompt-form-card">
        <div className="form-header">
          <h1>{isEdit ? 'Edit Prompt' : 'Create New Prompt'}</h1>
          <button onClick={() => navigate('/prompts')} className="btn-close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="id">Prompt ID</label>
            <input
              type="text"
              id="id"
              name="id"
              value={formData.id}
              onChange={handleChange}
              required
              disabled={isEdit}
              className="form-input"
            />
            <small>Unique identifier for this prompt (UUID)</small>
          </div>

          <div className="form-group">
            <label htmlFor="name">Prompt Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., care_report.system"
              className="form-input"
            />
            <small>Name of the prompt (e.g., "care_report.system")</small>
          </div>

          <div className="form-group">
            <label htmlFor="content">Prompt Content</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              required
              rows={15}
              placeholder="Enter the prompt content here..."
              className="form-textarea"
            />
            <small>Full content of the prompt</small>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/prompts')}
              className="btn btn-cancel"
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-submit" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? 'Update Prompt' : 'Create Prompt'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromptForm;
